import { ToastContainer } from "react-toastify";
import PageHeadings from '../components/PageHeadings';
import { filterFiltersByPage } from '../helpers/filterUtils';
import { useDashboardViews } from '../hooks/useDashboardViews';
import ResellerAppHeader from '../components/ResellerAppHeader';
import { pageFilterOptionsDefaultState } from '../consts/graphs';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PagesCommonProps, PagesList, getValidFilterNamesByPage, getDateRangeFiltersByPage } from '../helpers/pagesUtil';
import AppLoading from '../components/AppLoading';

const LOCAL_STORAGE_KEY = "selectedPage";
const FILTERS_STORAGE_KEY = "dashboardFilters";

const HomePage: React.FC = () => {
  const { dashboardViews, loading, error } = useDashboardViews();
  
  // Retrieve from localStorage, default to 1 if invalid
  const getStoredPage = () => {
    const storedPage = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedPage ? Number(storedPage) || 1 : 1;
  };

  const getStoredFilters = (): PagesCommonProps["filters"] => {
    try {
      const storedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (storedFilters) {
        const parsed = JSON.parse(storedFilters);
        
        return { ...pageFilterOptionsDefaultState, ...parsed };
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    return pageFilterOptionsDefaultState;
  };

  const saveFiltersToStorage = (filtersToSave: PagesCommonProps["filters"]) => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  };

  const [selectedPage, setSelectedPage] = useState<number>(getStoredPage);
  const [filters, setFiltersState] = useState<PagesCommonProps["filters"]>(getStoredFilters);

  const setFilters = useCallback((newFilters: PagesCommonProps["filters"] | ((prev: PagesCommonProps["filters"]) => PagesCommonProps["filters"])) => {
    setFiltersState(prevFilters => {
      const updatedFilters = typeof newFilters === 'function' ? newFilters(prevFilters) : newFilters;
      saveFiltersToStorage(updatedFilters);
      return updatedFilters;
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, selectedPage.toString());
  }, [selectedPage]);

  // Load filters from localStorage on mount
  useEffect(() => {
    const storedFilters = getStoredFilters();
    setFiltersState(storedFilters);
  }, []);

   // Ensure selectedPage is within valid range when dashboardViews change
   useEffect(() => {
    if (dashboardViews.length > 0 && selectedPage > dashboardViews.length) {
      setSelectedPage(1);
    }
  }, [dashboardViews, selectedPage]);

  // Get current dashboard
  const currentDashboard = useMemo(() => {
    if (loading || error || dashboardViews.length === 0) {
      return null;
    }
    return dashboardViews[selectedPage - 1] || null;
  }, [dashboardViews, selectedPage, loading, error]);

  const validFilterNames = useMemo(() => {
    let filterNames: string[] = [];
    
    if (!currentDashboard?.filters || currentDashboard.filters.length === 0) {
      filterNames = getValidFilterNamesByPage(selectedPage - 1);
    
      const pageIndex = selectedPage - 1;
      const { dateFilterNames, rangeFilterNames } = getDateRangeFiltersByPage(pageIndex);
      filterNames.push(...dateFilterNames, ...rangeFilterNames);
    } else {
      filterNames = currentDashboard.filters.map((filter: any) => filter.name);
      
      if (currentDashboard.filters.some((f: any) => f.name === 'datepicker')) {
        filterNames.push('startDate', 'endDate');
      }
      if (currentDashboard.filters.some((f: any) => f.name === 'rangepicker')) {
        filterNames.push('MinRange', 'MaxRange', 'MinRangePercent', 'MaxRangePercent');
      }
    }
    
    return filterNames;
  }, [currentDashboard, selectedPage]);

  const filteredFilters = useMemo(() => {
    return filterFiltersByPage(filters, validFilterNames);
  }, [filters, validFilterNames]);

  const getCurrentPage = () => {
    if (loading || error || dashboardViews.length === 0 || !currentDashboard) {
      return null;
    }

    // Map dashboard ID to page component (assuming IDs 1-6 map to pages 1-6)
    const pageIndex = currentDashboard.id - 1;
    const pages = PagesList({ filters: filteredFilters, setFilters, graphs: currentDashboard.graphs || [] });
    return pages[pageIndex] || null;
  };

  const Page = getCurrentPage();

  // Show loading state
  if (loading) {
    return (
      <>
      <ResellerAppHeader
        filters={filters}
        setFilters={setFilters}
        selectedPage={selectedPage}
        dashboardViews={dashboardViews}
        filteredFilters={filteredFilters}
        onClickLogo={() => setSelectedPage(1)}
      />
        <div className="app">
          <div className="dashboard-container">
            <AppLoading isLoading={true} />
          </div>
        </div>
        <ToastContainer />
      </>
    );
  }

  // Show error state
  if (error) {
    return (
      <>
        <ResellerAppHeader
          filters={filters}
          setFilters={setFilters}
          selectedPage={selectedPage}
          dashboardViews={dashboardViews}
          filteredFilters={filteredFilters}
          onClickLogo={() => setSelectedPage(1)}
        />
        <div className="app">
          <div className="dashboard-container">
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              Error: {error}
            </div>
          </div>
        </div>
        <ToastContainer />
      </>
    );
  }
  
  return (
    <>
      <div className="pdf-export-target">
        <ResellerAppHeader
          filters={filters}
          setFilters={setFilters}
          selectedPage={selectedPage}
          dashboardViews={dashboardViews}
          filteredFilters={filteredFilters}
          onClickLogo={() => setSelectedPage(1)}
        />
        <PageHeadings
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          dashboardViews={dashboardViews}
        />
        <div className="app">
          <div className="dashboard-container">{Page}</div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default HomePage;
